import type { QuizData } from '../types'

/**
 * The skip-path UTM stamp — `{ [quizData.variable]: quizData.value }` — or an empty
 * object when the quiz record doesn't configure one.
 *
 * Marks traffic that BYPASSED the quiz (gate skip button, or a `skip: true` answer)
 * so GAM can separate skippers from completers. Every lead-gen op sets this to
 * utm_medium=skip-quiz; the value is read from the quiz record, never hardcoded.
 *
 * Callers must apply this AFTER copying the inbound UTMs, so it overrides them —
 * relabelling the inbound `utm_medium=cpc` to `skip-quiz` is the entire point.
 *
 * Deliberately NO fallback stamp when the record omits the fields. The original
 * implementation (lost in the 2026-05-18 `main` reset and restored here) fell back
 * to `utm_content=skip`, which silently clobbered a real inbound utm_content on
 * every skip and corrupted creative-level attribution. Unconfigured quizzes now
 * simply forward their inbound UTMs untouched. See DEV-1418.
 *
 * Two guards on `variable`, because it is a free-text field an operator types into
 * the Publinker quiz form and the stamp is applied LAST (it wins over the inbound
 * value):
 *   - trimmed, so a stray space can't mint an unusable `"utm_medium "` key;
 *   - click-through IDs are refused outright. A typo'd `gclid` in that field would
 *     overwrite the real Google Ads click ID on every skip redirect and silently
 *     break conversion attribution — the same class of invisible data loss this
 *     card exists to fix. utm_* keys stay freely configurable.
 */
const BLOCKED_STAMP_KEYS = new Set(['gclid', 'fbclid', 'gbraid', 'wbraid', 'sub_id'])

export function skipUtmParams(
  quizData: Pick<QuizData, 'variable' | 'value'> | null | undefined
): Record<string, string> {
  const variable = quizData?.variable?.trim()
  const value = quizData?.value?.trim()

  if (!variable || !value) return {}
  if (BLOCKED_STAMP_KEYS.has(variable.toLowerCase())) return {}

  return { [variable]: value }
}

/**
 * Build the post-quiz redirect URL: <base>?<query>#<hash>.
 *
 * Undefined values in either bag are filtered out so call sites can pass optional
 * URL params without pre-cleaning. Hash params are emitted in insertion order,
 * joined by '&', so the shape is `#l_e=...&l_s=...` when both are present.
 *
 * Used by Gate.vue's handleSubmit (with both l_e and l_s) and handleSkip (with
 * l_s only). Consumed downstream by leadclump-flow, whose hash parser validates
 * l_s against /^[A-Za-z0-9-]{8,64}$/ — generateSessionId in analytics/session.ts
 * must produce values that satisfy this regex.
 */
export function buildRedirectUrl(
  baseUrl: string | undefined,
  additionalParams?: Record<string, string | undefined>,
  hashParams?: Record<string, string | undefined>
): string {
  const url = baseUrl || '';

  const filteredParams: Record<string, string> = Object.entries(additionalParams || {})
    .filter(([, value]) => value !== undefined)
    .reduce((acc, [key, value]) => {
      acc[key] = value as string;
      return acc;
    }, {} as Record<string, string>);

  const filteredHash: Record<string, string> = Object.entries(hashParams || {})
    .filter(([, value]) => value !== undefined)
    .reduce((acc, [key, value]) => {
      acc[key] = value as string;
      return acc;
    }, {} as Record<string, string>);

  const queryParams = new URLSearchParams(filteredParams);

  let hashParamsString = '';
  if (Object.keys(filteredHash).length > 0) {
    // Encode keys and values defensively. Today's hash values (sha256 hex, UUIDs) are
    // safe characters, but the signature accepts arbitrary string|undefined — a future
    // caller passing user-controlled data should not be able to smuggle `&key=value`.
    hashParamsString = Object.entries(filteredHash)
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join('&');
    hashParamsString = `#${hashParamsString}`;
  }

  return `${url}?${queryParams.toString()}${hashParamsString}`;
}
